import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchCancellationReasons,
  createCancellationReason,
  updateCancellationReason,
  deleteCancellationReason,
  type ListCancellationReasonsQuery,
  type CreateCancellationReasonPayload,
  type UpdateCancellationReasonPayload,
} from '@/services/api/cancellationReasons.api';

export function useCancellationReasons(
  query: ListCancellationReasonsQuery,
  enabled = true,
) {
  return useQuery({
    queryKey: ['cancellation-reasons', query],
    queryFn: () => fetchCancellationReasons(query),
    staleTime: 30_000,
    enabled: enabled && !!query.propertyId,
  });
}

export function useCreateCancellationReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateCancellationReasonPayload) =>
      createCancellationReason(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['cancellation-reasons'],
      });
    },
  });
}

export function useUpdateCancellationReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: UpdateCancellationReasonPayload;
    }) => updateCancellationReason(id, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['cancellation-reasons'],
      });
    },
  });
}

export function useDeleteCancellationReason() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteCancellationReason(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['cancellation-reasons'],
      });
    },
  });
}
